import { useEffect, useMemo, useRef } from 'react'

import { useWindow, useQueryResult } from '@krp/client-runtime'
import {
  ONEC_SUPPLY_WINDOWS,
  SupplyCreateScreen,
  buildCreate,
  commandDismissed,
  createFormChanged,
  createFormReset,
  makeSelectSupplyCreateVm,
  normalizeRid,
  onecStructure,
  reloadStructure,
  selectStructureWarehouses,
  supplyCommandRequested,
  supplyCommandResendRequested,
  supplyCreateIntent,
  type SupplyCreateActions,
  type SupplyResourceArgs,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { localToday } from './supplyDates'

/** Склады базы из ответа структуры — те же, что в выпадающем списке. */
const structureWarehouses = (result: { data?: { data: Parameters<typeof selectStructureWarehouses>[0] } }) =>
  result.data === undefined ? [] : selectStructureWarehouses(result.data.data)

/**
 * Binding «Новой поставки»: база — из адреса окна, склады — структура ЭТОЙ
 * базы (ONEC-STRUCT), ревизия настроек для команды — из ответа этой же базы. Окно «Поставка из
 * накладной» — тот же binding: файл из адреса окна, строки заполняет пакет после создания.
 */
export function SupplyCreateBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const invoiceFileId = window.params.fileId ?? ''
  const today = useMemo(() => localToday(), [])
  const args = useMemo<SupplyResourceArgs | null>(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const warehouses = useQueryResult(onecStructure, args)
  const intent = useAppSelector((state) => state.onec.supplies)
  const selectVm = useMemo(() => makeSelectSupplyCreateVm(), [])
  const vm = selectVm({ resourceId, today, warehouses, intent, invoiceFileId })
  const latest = useRef({ vm, warehouses })
  useEffect(() => {
    latest.current = { vm, warehouses }
  }, [vm, warehouses])

  const actions = useMemo<SupplyCreateActions>(() => {
    const version = () => latest.current.warehouses.data?.configurationVersion ?? ''
    return {
      changed: (patch) => dispatch(createFormChanged({ resourceId, patch, today })),
      submit: () => {
        // Файл накладной из адреса негоден — не создавать «просто поставку» вместо поставки из накладной.
        if (!latest.current.vm.canSubmit) return
        const built = buildCreate(resourceId, latest.current.vm.form, structureWarehouses(latest.current.warehouses))
        if (!built.ok) return
        dispatch(supplyCommandRequested(supplyCreateIntent(resourceId, built.data, version(), invoiceFileId)))
      },
      // «Повторить» после отказа 1С: склады — структура базы, служба читает её из 1С на лету (RP-LSB2).
      reloadWarehouses: () => dispatch(reloadStructure(resourceId)),
      openCreated: () => {
        const created = latest.current.vm.created
        if (created === null) return
        const key = latest.current.vm.command?.key
        dispatch(createFormReset({ resourceId }))
        if (key !== undefined) dispatch(commandDismissed({ key }))
        window.replaceWith(ONEC_SUPPLY_WINDOWS.supply, { rid: resourceId, supplyId: created.id })
      },
      resend: (key) => dispatch(supplyCommandResendRequested({ key })),
      dismiss: (key) => dispatch(commandDismissed({ key })),
      close: window.close,
    }
  }, [dispatch, invoiceFileId, resourceId, today, window])
  return <SupplyCreateScreen vm={vm} actions={actions} />
}
